import * as Y from 'yjs';
import { ObjectType } from '../../../Types/base';
import { Dispatch, MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { ORIGIN_CACHE_REPLAY, ORIGIN_LOCAL, ORIGIN_REMOTE } from '../constants';
import {
	WhiteboardSignalREvent,
	WhiteboardSignalREventType,
	WhiteboardYjsCacheData,
	WhiteboardYjsUpdate,
} from '../../../Types/whiteboard';
import { base64ToUint8Array } from '../../../Utils/stringUtils';
//import { restoreElements } from '@excalidraw/excalidraw';
import { purgeByIds, syncElementsWithYArray, versionHash, yToExcalidrawElements } from '../utils';
import { User } from '../../../Types/user';
import { HubKey } from '../../../Contexts/signalrContext';
import { WhiteboardAction } from '../reducer';
import { useExcalidraw } from './useExcalidraw';

export const useWhiteboardYjs = (
	whiteboardId: number | undefined,
	user: User | undefined,
	objectType: ObjectType,
	objectId: number,
	sendMessage: (key: HubKey, message: unknown) => void,
	isHydrating: boolean,
	dispatch: Dispatch<WhiteboardAction>,
	userIsActiveRef: MutableRefObject<boolean>
) => {
	const yjsDocRef = useRef<Y.Doc | null>(null);
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	const yElementsRef = useRef<Y.Array<any> | null>(null);
	const yUndoManagerRef = useRef<Y.UndoManager | null>(null);
	const pendingPaintRef = useRef<NodeJS.Timeout | null>(null);
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	const excalidrawRef = useRef<any>(null);
	//eslint-disable-next-line @typescript-eslint/no-explicit-any
	const unmergedLocalElementsRef = useRef<any[] | null>(null);

	const { restoreElements } = useExcalidraw();

	// Helper function to paint elements from YJS
	const paintFromYInternal = useCallback(
		(fromUndoRedo: boolean = false, initial: boolean = false) => {
			if (!excalidrawRef.current) {
				console.warn('Failed to apply Yjs update to Excalidraw: Excalidraw ref not ready yet.');
				return;
			}
			if (!yElementsRef.current) {
				console.warn('Failed to apply Yjs update to Excalidraw: Yjs elements not ready yet, cannot paint from Yjs');
				return;
			}

			if (unmergedLocalElementsRef.current && unmergedLocalElementsRef.current.length > 0) {
				console.log('Merging unmerged local elements into Yjs before painting from Yjs', {
					count: unmergedLocalElementsRef.current.length,
				});
				syncElementsWithYArray(yElementsRef.current, unmergedLocalElementsRef.current);
				unmergedLocalElementsRef.current = null;
			}

			const getElementsToApply = () => {
				if (initial) {
					if (!restoreElements) {
						console.error('Cannot restore elements during initial paint from Yjs: restoreElements not ready yet.');
						return yToExcalidrawElements(yElementsRef.current!);
					}
					return restoreElements(yToExcalidrawElements(yElementsRef.current!), null);
				}
				return yToExcalidrawElements(yElementsRef.current!);
			};

			const els = getElementsToApply();
			const h = versionHash(els);

			if (fromUndoRedo) {
				dispatch({ type: 'UPDATE_LAST_VERSION_HASH', payload: h });
			} else {
				dispatch({ type: 'ADD_REMOTE_HASH', payload: h });
			}

			els.sort((a, b) => ((a.index ?? 0) < (b.index ?? 0) ? -1 : 1));
			excalidrawRef.current.updateScene({ elements: els, commitToHistory: false });
			console.log('Applied Yjs update to Excalidraw', {
				versionHash: h,
				fromUndoRedo,
				yVersionHash: versionHash(yToExcalidrawElements(yElementsRef.current)),
			});
		},
		[dispatch, restoreElements]
	);

	const paintFromY = useCallback(
		(fromUndoRedo: boolean = false, initial: boolean = false) => {
			if (pendingPaintRef.current) {
				//console.log('Discarding pending update to Excalidraw from Yjs due to new update request');
				clearTimeout(pendingPaintRef.current);
			}

			if (fromUndoRedo) {
				console.log('Applying undo/redo Yjs update to Excalidraw immediately because it is from local undo/redo');
				paintFromYInternal(fromUndoRedo, initial);
			}

			if (!userIsActiveRef.current) {
				console.log(
					'Applying Yjs update to Excalidraw immediately because user is inactive and the update will not block anything'
				);
				paintFromYInternal(fromUndoRedo, initial);
				return;
			}

			//console.log('Scheduling update to Excalidraw from Yjs in 100ms because user is active');
			pendingPaintRef.current = setTimeout(() => {
				paintFromY(fromUndoRedo, initial);
			}, 100);
		},
		[paintFromYInternal]
	);

	// Initialize YJS document
	useEffect(() => {
		if (!whiteboardId) return;

		console.log('Initializing Yjs document for whiteboard:', whiteboardId);

		const yjsDoc = new Y.Doc();
		//eslint-disable-next-line @typescript-eslint/no-explicit-any
		const yElements = yjsDoc.getArray<Y.Map<any>>('elements');

		const undoManager = new Y.UndoManager(yElements, {
			trackedOrigins: new Set([ORIGIN_LOCAL]),
		});

		yjsDocRef.current = yjsDoc;
		yElementsRef.current = yElements;
		yUndoManagerRef.current = undoManager;

		// yjsDoc.on('beforeTransaction', (t) => {
		// 	console.log('Yjs transaction start', { origin: t.origin, transaction: t });
		// });

		// yjsDoc.on('afterTransaction', (t) => {
		// 	console.log('Yjs transaction end', { origin: t.origin, transaction: t });
		// });

		// Listen for Yjs updates
		//eslint-disable-next-line @typescript-eslint/no-explicit-any
		yjsDoc.on('update', (update: Uint8Array, origin: any) => {
			const currentVersionHash = versionHash(yToExcalidrawElements(yElementsRef.current!));
			if (origin === ORIGIN_LOCAL || origin === yUndoManagerRef.current) {
				console.log('Yjs update detected from local origin, will send via SignalR to server to notifiy other clients', {
					origin,
					versionHash: currentVersionHash,
				});
				const updateMessage = {
					Update: Array.from(update),
					UserId: user?.Id,
					Timestamp: new Date(),
				};

				sendMessage('WHITEBOARD', {
					EventType: WhiteboardSignalREventType.VJS_Update,
					ObjectType: objectType,
					ObjectId: objectId,
					Content: updateMessage,
					WhiteboardId: whiteboardId,
				} satisfies WhiteboardSignalREvent);
			}
		});

		//eslint-disable-next-line @typescript-eslint/no-explicit-any
		const handleYElementsChange = (events: Y.YArrayEvent<any>[]) => {
			const anyRemote = events.some((e) => e.transaction?.origin !== ORIGIN_LOCAL);
			const anyUndoRedo = events.some((e) => e.transaction?.origin === yUndoManagerRef.current);

			versionHash(yToExcalidrawElements(yElementsRef.current!)); //just to log warnings about missing IDs

			if (!anyRemote) {
				console.log(
					'Yjs elements changed, but only with local origin which should already be applied, so no need to update Excalidraw'
				);
				return;
			}

			const originType = anyUndoRedo ? 'undo/redo' : 'remote';
			if (!excalidrawRef.current) {
				console.warn(`Yjs elements changed via ${originType} origin, but Excalidraw ref is not ready yet`);
				return;
			}

			if (isHydrating) {
				console.warn(
					`Yjs elements changed via ${originType} origin, but we are still hydrating, so we cannot update Excalidraw. THAT MEANS WE MISSED A REMOTE UPDATE!!`
				);
				return;
			}

			paintFromY(anyUndoRedo);
		};

		// Listen for array changes to update Excalidraw
		yElements.observeDeep(handleYElementsChange);

		return () => {
			yElements.unobserveDeep(handleYElementsChange);
			yjsDoc.destroy();
			if (pendingPaintRef.current) clearTimeout(pendingPaintRef.current);
		};
	}, [objectType, objectId, sendMessage, user?.Id, whiteboardId, paintFromY, isHydrating]);

	// Memoize the methods to avoid recreating the hook object
	const handleChange = useCallback(
		(
			//eslint-disable-next-line @typescript-eslint/no-explicit-any
			elements: readonly any[],
			//eslint-disable-next-line @typescript-eslint/no-explicit-any
			appState: any,
			lastVersionHash: string,
			recentRemoteHashes: string[],
			hasJoinedSignalR: boolean,
			allowEdit: boolean,
			trackActivity: (schedule: boolean) => void
		) => {
			if (!excalidrawRef.current) {
				console.log('Excalidraw handleChange: ExcalidrawRef not initialized yet, cannot update Yjs');
				if (elements.length > 0) {
					console.error(
						'Excalidraw handleChange: Elements present but ExcalidrawRef not initialized yet, this should not happen'
					);
				}
				return;
			}

			if (!yElementsRef.current) {
				console.log('Excalidraw handleChange: Yjs elements array not initialized yet, cannot update Yjs');
				if (elements.length > 0) {
					console.error(
						'Excalidraw handleChange: Elements present but Yjs elements array not initialized yet, this should not happen'
					);
				}
				return;
			}

			if (!yjsDocRef.current) {
				console.log('Excalidraw handleChange: Yjs document not initialized yet, cannot update Yjs');
				if (elements.length > 0) {
					console.error(
						'Excalidraw handleChange: Elements present but Yjs document not initialized yet, this should not happen'
					);
				}
				return;
			}

			if (!allowEdit) {
				console.log('Excalidraw handleChange: Editing disabled, skipping Yjs update');
				return;
			}

			if (!hasJoinedSignalR) {
				console.warn('Excalidraw handleChange: WARNING SignalR not initialized yet');
			}

			const h = versionHash(elements);
			const noChange = h === lastVersionHash;
			const remoteChange = recentRemoteHashes.includes(h);

			if (noChange) {
				console.log('Excalidraw handleChange: No changes detected since latest local update, skipping updating Yjs', h);
				return;
			}

			if (remoteChange) {
				const recentIndex = recentRemoteHashes.indexOf(h);
				console.log(
					'Excalidraw handleChange: The changes matches one of the recent remote updates, assuming this is a local echo of it, skipping updating Yjs',
					recentIndex,
					h
				);
				return;
			}

			//performance-wise it would be better to have this at the top,
			// but we need to know if any local changes has been made, to make sure they are applied before any repaint of excalidraw triggered by remote updates
			if (appState.cursorButton === 'down') {
				//	console.log('Excalidraw handleChange: User is currently drawing, skipping Yjs update');
				//eslint-disable-next-line @typescript-eslint/no-explicit-any
				unmergedLocalElementsRef.current = elements as any[];
				trackActivity(false);
				return;
			}

			//eslint-disable-next-line @typescript-eslint/no-explicit-any
			const currById = new Map(yElementsRef.current.toArray().map((e: any) => [e.id, e]));

			const changedIds = elements
				.filter((e) => {
					const c = currById.get(e.id);
					return !c || JSON.stringify(c) !== JSON.stringify(e);
				})
				.map((e) => e.id);

			if (changedIds.length === 0) {
				console.log('Excalidraw handleChange: No actual element changes detected, skipping updating Yjs');
				return;
			}

			const yPreviousVersionHash = versionHash(yToExcalidrawElements(yElementsRef.current));
			syncElementsWithYArray(yElementsRef.current, elements);
			dispatch({ type: 'UPDATE_LAST_VERSION_HASH', payload: h });

			trackActivity(true);
			console.log('Excalidraw handleChange: Yjs updated!', { versionHash: h, yPreviousVersionHash });
		},
		[dispatch]
	);

	const applyYjsUpdate = useCallback(
		(update: WhiteboardYjsUpdate) => {
			if (update && update.Update && update.UserId !== user?.Id && yjsDocRef.current) {
				const before = Y.encodeStateVector(yjsDocRef.current!);
				if (before.length === 0) {
					console.log("APPLYING YJS UPDATE BUT OUR DOCUMENT IS EMPTY, SHOULDN'T HAPPEN AFTER INITIAL LOAD");
				}

				const updateArray = new Uint8Array(update.Update);
				Y.applyUpdate(yjsDocRef.current, updateArray, ORIGIN_REMOTE);
			}
		},
		[user?.Id]
	);

	const applySnapshot = useCallback((yjsStateUpdate: string) => {
		if (yjsDocRef.current) {
			const bin = base64ToUint8Array(yjsStateUpdate);
			Y.applyUpdate(yjsDocRef.current, bin, ORIGIN_CACHE_REPLAY);
			console.log('YArray content after snapshot load:', yElementsRef.current!.toArray());
		}
	}, []);

	const applyPendingUpdates = useCallback((pendingUpdates: WhiteboardYjsCacheData[]) => {
		if (yjsDocRef.current && pendingUpdates.length > 0) {
			console.log('Applying', pendingUpdates.length, 'pending cached updates');
			let index = 1;
			for (const u of pendingUpdates) {
				const yjsUpdate = base64ToUint8Array(u.yjsUpdate);
				Y.applyUpdate(yjsDocRef.current!, yjsUpdate, ORIGIN_CACHE_REPLAY);
				console.log('Applied update', index, 'of', pendingUpdates.length);
				index++;
			}
		}
	}, []);

	const purgeDeletedElements = useCallback(() => {
		if (yElementsRef.current) {
			//eslint-disable-next-line @typescript-eslint/no-explicit-any
			const curr = yElementsRef.current.toArray() as any[];
			const zombieIds = curr.filter((e) => e?.isDeleted).map((e) => e.id);
			if (zombieIds.length > 0) {
				console.log('Purging deleted zombies from Yjs:', zombieIds.length);
				purgeByIds(yElementsRef.current, zombieIds);
			}
		}
	}, []);

	const undo = useCallback(() => {
		if (yUndoManagerRef.current) {
			yUndoManagerRef.current.undo();
		}
	}, []);

	// Return a memoized object to avoid recreating it on every render
	return useMemo(
		() => ({
			yjsDocRef,
			yElementsRef,
			yUndoManagerRef,
			excalidrawRef,
			paintFromY,
			handleChange,
			applyYjsUpdate,
			applySnapshot,
			applyPendingUpdates,
			purgeDeletedElements,
			undo,
		}),
		[paintFromY, handleChange, applyYjsUpdate, applySnapshot, applyPendingUpdates, purgeDeletedElements, undo]
	);
};
